import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreateStockReturnMutation, useListStockOutsQuery, useGetStockOutQuery } from "../../stockApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateReturn } from "../../useStockPermissions";
import PartyPicker from "../../components/PartyPicker";
import LineItemsEditor, { emptyRow, computeTotals } from "../../components/LineItemsEditor";
import { useFillMissingUnits } from "../../components/lineUnits";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const columns = { showRate: true, showVat: false, showRemarks: true, showReason: false, showMaxQty: true };
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const StockReturnForm = () => {
  const navigate = useNavigate();
  const canCreate = useCanCreateReturn();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createStockReturn, { isLoading: isSaving }] = useCreateStockReturnMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [returnType, setReturnType] = useState("STOCK_OUT_BASED");
  const [selectedStockOutGuid, setSelectedStockOutGuid] = useState("");
  const [supplierOption, setSupplierOption] = useState(null);
  const [returnDate, setReturnDate] = useState(toDateInput(new Date()));
  const [returnReason, setReturnReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([emptyRow()]);

  // 2026-10-07: was a plain <select> of the newest 200 only - older Stock-Outs
  // (ISO-MOMBASA-000033, -000055) were missing and nothing could be typed. Now the
  // whole branch list loads into a dropdown you can type in to filter.
  const { data: stockOutPage, isFetching: isFetchingStockOuts } = useListStockOutsQuery(
    { branchId: branchId ? Number(branchId) : undefined, pageSize: 5000 },
    { skip: !branchId || returnType !== "STOCK_OUT_BASED" }
  );
  const stockOutOptions = useMemo(
    () =>
      (stockOutPage?.items || []).map((s) => ({
        value: s.recordGuid,
        label: `${s.stockOutNo} - ${s.issuedToEmployeeName || s.issuedToCustomerName || ""}`,
      })),
    [stockOutPage]
  );
  const { data: selectedStockOut } = useGetStockOutQuery(selectedStockOutGuid, { skip: !selectedStockOutGuid });
  const fillMissingUnits = useFillMissingUnits();

  useEffect(() => {
    if (!selectedStockOut) return;
    const outRows = selectedStockOut.lines
      .filter((l) => Number(l.issuedQty) - Number(l.returnedQty) > 0)
      .map((l) => ({
        ...emptyRow(),
        productCode: l.productCode,
        productName: l.productName,
        unit: l.unit || "",
        qty: Number(l.issuedQty) - Number(l.returnedQty),
        rate: l.unitRate ?? "",
        maxQty: Number(l.issuedQty) - Number(l.returnedQty),
        locked: true,
        refId: l.lineId,
      }));
    setLines(outRows);
    // Locked rows hide the picker, so this is the only place a returned
    // line's unit can come from when the Stock-Out itself didn't store one.
    fillMissingUnits(outRows, selectedStockOut.branchId).then((filled) => {
      if (filled !== outRows) setLines(filled);
    });
  }, [selectedStockOut, fillMissingUnits]);

  const handleTypeChange = (nextType) => {
    setReturnType(nextType);
    setSelectedStockOutGuid("");
    setSupplierOption(null);
    setLines([emptyRow()]);
  };

  const totals = computeTotals(lines, columns);
  const readOnly = !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    if (returnType === "STOCK_OUT_BASED" && !selectedStockOutGuid) return toast.error("Select the Stock-Out this return is against");
    if (!remarks.trim()) return toast.error("Remarks are required");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");

    const supplierName = returnType === "DIRECT_SUPPLIER" ? (supplierOption?.customer?.custName || supplierOption?.label || "").trim() : null;
    if (returnType === "DIRECT_SUPPLIER" && !supplierName) return toast.error("Supplier name is required for a direct-to-supplier return");

    const body = {
      branchId: Number(branchId),
      returnType,
      stockOutRecordGuid: returnType === "STOCK_OUT_BASED" ? selectedStockOutGuid : null,
      supplierId: returnType === "DIRECT_SUPPLIER" ? supplierOption?.customer?.custId ?? null : null,
      supplierBranchId: returnType === "DIRECT_SUPPLIER" ? supplierOption?.customer?.branchId ?? null : null,
      supplierName,
      returnDate: new Date(returnDate).toISOString(),
      totalAmount: totals.netAmount,
      returnReason: returnReason.trim() || null,
      remarks: remarks.trim(),
      lines: validLines.map((l) => ({
        stockOutLineId: l.refId || null,
        productCode: Number(l.productCode),
        productName: l.productName,
        returnedQty: Number(l.qty),
        unit: l.unit || null,
        unitRate: l.rate === "" ? null : Number(l.rate),
        lineAmount: l.rate === "" ? null : (Number(l.qty) || 0) * (Number(l.rate) || 0),
        remarks: l.remarks || null,
      })),
    };

    try {
      const created = await createStockReturn(body).unwrap();
      toast.success("Stock return posted");
      navigate(paths.stockReturnDetail(created.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not post the return");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockReturns} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Returns</Link>
              <h4>New Stock Return</h4>
            </div>
          </div>
        </div>

        {readOnly && <div className="alert alert-info">You have read-only access. Posting a return requires Stock.CreateReturn.</div>}

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex gap-4 mb-3">
              <div className="form-check">
                <input type="radio" className="form-check-input" id="type-stockout" checked={returnType === "STOCK_OUT_BASED"} onChange={() => handleTypeChange("STOCK_OUT_BASED")} />
                <label className="form-check-label" htmlFor="type-stockout">Against a Stock-Out</label>
              </div>
              <div className="form-check">
                <input type="radio" className="form-check-input" id="type-direct" checked={returnType === "DIRECT_SUPPLIER"} onChange={() => handleTypeChange("DIRECT_SUPPLIER")} />
                <label className="form-check-label" htmlFor="type-direct">Direct to Supplier</label>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select className="form-control" value={branchId} onChange={(e) => { setBranchId(e.target.value); setSelectedStockOutGuid(""); }} disabled={readOnly}>
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>

              {returnType === "STOCK_OUT_BASED" ? (
                <div className="col-md-5 mb-3">
                  <label className="form-label">Stock-Out</label>
                  <Select
                    classNamePrefix="react-select"
                    options={stockOutOptions}
                    value={stockOutOptions.find((o) => o.value === selectedStockOutGuid) || null}
                    onChange={(opt) => setSelectedStockOutGuid(opt?.value || "")}
                    isClearable
                    isLoading={isFetchingStockOuts}
                    isDisabled={readOnly || !branchId}
                    placeholder="--Select or type a Stock-Out no / name--"
                    noOptionsMessage={() => "No Stock-Out matches"}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              ) : (
                <div className="col-md-5 mb-3">
                  <label className="form-label">Supplier / Party (Customer Master)</label>
                  <PartyPicker mode="all-branches" value={supplierOption} onChange={setSupplierOption} disabled={readOnly} creatable />
                </div>
              )}

              <div className="col-md-2 mb-3">
                <label className="form-label">Return Date</label>
                <input type="date" className="form-control" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Return Reason</label>
                <input type="text" className="form-control" value={returnReason} onChange={(e) => setReturnReason(e.target.value)} disabled={readOnly} />
              </div>
            </div>

            <hr />
            <h5 className="mb-3">Returned Lines</h5>
            <LineItemsEditor
              branchId={branchId ? Number(branchId) : undefined}
              rows={lines}
              onChange={setLines}
              columns={columns}
              disabled={readOnly || (returnType === "STOCK_OUT_BASED" && !selectedStockOutGuid)}
            />

            <div className="row mt-3">
              <div className="col-md-8 mb-3">
                <label className="form-label">Remarks (required)</label>
                <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3 d-flex align-items-end justify-content-end">
                <div>Total Amount: <strong>{totals.netAmount.toFixed(2)}</strong></div>
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockReturns)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockReturnForm;
